import dayjs from 'dayjs';
import 'dayjs/locale/es';

import type { CancelInvoiceOutcome } from '../../lib/cancellation';
import { displayFiscalName } from '../../lib/displayName';
import { formatAmount } from '../../lib/money';
import type { Retention } from '../../types';
import { CURRENCIES, PAYMENT_METHODS } from '../constants/payment';
import type { IncomeMonth } from './qrOwnerSummary';
import { matchesAnyField } from './search';

/**
 * Las tres listas de «Mis facturas»: lo que el usuario emitió, lo que recibió y las
 * constancias de retenciones que le hicieron. Solo una emitida tiene detalle propio;
 * las otras dos se abren en su PDF o su XML.
 */
export type MyInvoiceKind = 'emitida' | 'recibida' | 'retencion';

export type MyInvoicesTab = 'emitidas' | 'recibidas' | 'retenciones';

export const MY_INVOICES_TABS: { value: MyInvoicesTab; label: string }[] = [
  { value: 'emitidas', label: 'Emitidas' },
  { value: 'recibidas', label: 'Recibidas' },
  { value: 'retenciones', label: 'Retenciones' },
];

/**
 * Un documento de «Mis facturas». Casi siempre una factura que el usuario emitió, la haya
 * timbrado con heru o no; con `kind` también una que recibió o una constancia de
 * retenciones, donde la contraparte es quien la emitió (`issuerName`).
 *
 * `cancelando`: la cancelación está pedida y el SAT no la resuelve. Mientras
 * tanto la factura SIGUE VIGENTE ante el SAT, así que no se pinta cancelada.
 */
export type MyInvoiceStatus = 'vigente' | 'cancelando' | 'cancelada';

/** c_TipoDeComprobante del SAT. */
export type MyInvoiceType = 'I' | 'E' | 'P' | 'T' | 'N';

export type MyInvoicePaymentMethod = (typeof PAYMENT_METHODS)[number]['code'];

/**
 * De dónde es: la timbró esta cuenta con heru, la timbró heru desde otra cuenta
 * con el mismo RFC, o solo la trae el SAT de lo que se timbró en otro lado.
 */
export type MyInvoiceSource = 'heru' | 'otra_cuenta' | 'externa';

export interface MyInvoice {
  kind: MyInvoiceKind;
  uuid: string;
  /** El mes (`YYYY-MM`) en el que el servidor la devuelve; con él se vuelve a pedir si no está en caché. */
  period: string;
  receiverName: string;
  receiverRfc: string;
  /** Solo en recibidas y retenciones: en una emitida quien emite es el usuario. */
  issuerName?: string;
  issuerRfc?: string;
  description: string;
  total: number;
  /** c_Moneda del comprobante; pesos cuando el servidor no la sabe o es `XXX` (P y T). */
  currency: string;
  /** Sin tipo (un servidor que todavía no lo manda) no se adivina: la factura se pinta sin signo. */
  type?: MyInvoiceType;
  paymentMethod?: MyInvoicePaymentMethod;
  /** La timbró heru, desde esta cuenta o desde otra con el mismo RFC: lleva el sello. */
  stampedByHeru: boolean;
  /** Solo lo que timbró esta cuenta (`heru`) admite registrar pagos desde aquí. */
  source: MyInvoiceSource;
  issuedAt: string;
  status: MyInvoiceStatus;
  cancelledAt?: string;
  /**
   * El trámite de cancelación según el servidor (`cancellation_status`). Sin él (nunca se
   * pidió desde el Go, o un servidor que todavía no lo manda) decide lo que recuerda este
   * navegador (`withRequestedCancellations`).
   */
  cancellation?: CancelInvoiceOutcome['cancellationStatus'];
  /** `gs://` si la timbró el Go (se firma al abrirla), URL pública si la timbró el NestJS. */
  pdfUrl?: string;
  xmlUrl?: string;
  /** En un complemento, las PPD que paga; en una PPD, los complementos que la pagan. */
  related: MyInvoiceRelation[];
}

/**
 * El otro lado de un pago en parcialidades. `period` es el mes en el que el
 * listado lo trae (el de su `issued_at`, con el corte en UTC del servidor): con
 * él se abre aunque viva en otro mes. Sin `period` no se puede abrir, porque el
 * usuario todavía no lo tiene a la vista.
 */
export interface MyInvoiceRelation {
  uuid: string;
  /** `I`: la PPD que este complemento paga. `P`: un complemento que paga esta PPD. */
  type: 'I' | 'P';
  issuedAt?: string;
  period?: string;
  /** El total de la PPD; en un complemento lo que cuenta es `amountPaid`. */
  total?: number;
  amountPaid?: number;
  installment?: number;
  /** Lo que quedó por cobrar de la PPD después de ese pago. */
  remainingBalance?: number;
  paymentDate?: string;
}

/**
 * Lo que trae cada «emitida» o «recibida» de `GET /invoices/profile/invoices-unified`
 * (heru-fiscal-engine, `internal/modules/invoicing/domain.go`). Las emitidas mezclan
 * dos fuentes: lo timbrado con heru (`IS_HERU_INVOICE`, de `invoicing_service`; lo
 * del Go llega porque el proyector lo copia cada minuto y las de prueba no se
 * copian) y lo que la extracción del SAT encontró emitido por el usuario. Las
 * recibidas solo salen de la extracción.
 */
export interface UnifiedEmittedInvoice {
  uuid?: string | null;
  total?: number | null;
  currency?: string | null;
  payment_method?: string | null;
  invoice_type?: string | null;
  issued_at?: string | null;
  receiver_name?: string | null;
  receiver_rfc?: string | null;
  issuer_name?: string | null;
  issuer_rfc?: string | null;
  description?: string | null;
  xml?: string | null;
  pdf?: string | null;
  status?: string | null;
  cancelled_at?: string | null;
  cancellation_status?: string | null;
  IS_HERU_INVOICE?: boolean;
  invoice_source?: string | null;
  related?: UnifiedInvoiceRelation[] | null;
}

/** `related` de invoices-unified (heru-fiscal-engine, `InvoiceRelation`). */
export interface UnifiedInvoiceRelation {
  uuid?: string | null;
  invoice_type?: string | null;
  issued_at?: string | null;
  total?: number | null;
  amount_paid?: number | null;
  installment_number?: number | null;
  remaining_balance?: number | null;
  payment_date?: string | null;
}

/** Los pagos de una PPD en el orden en que ocurrieron: por parcialidad y, sin ella, por fecha de pago. */
const byPaymentOrder = (a: MyInvoiceRelation, b: MyInvoiceRelation) =>
  (a.installment ?? Number.MAX_SAFE_INTEGER) - (b.installment ?? Number.MAX_SAFE_INTEGER) ||
  (a.paymentDate ?? '').localeCompare(b.paymentDate ?? '');

const toRelation = (raw: UnifiedInvoiceRelation): MyInvoiceRelation | null => {
  const type = raw.invoice_type?.trim().toUpperCase();
  if (!raw.uuid || (type !== 'I' && type !== 'P')) return null;
  return {
    uuid: raw.uuid,
    type,
    issuedAt: raw.issued_at || undefined,
    period: raw.issued_at ? periodKeyOf(currentPeriod(new Date(raw.issued_at))) : undefined,
    total: raw.total ?? undefined,
    amountPaid: raw.amount_paid ?? undefined,
    installment: raw.installment_number ?? undefined,
    remainingBalance: raw.remaining_balance ?? undefined,
    paymentDate: raw.payment_date || undefined,
  };
};

/**
 * La auto-factura que heru le emite al usuario por su suscripción (tabla
 * `heru_invoice`) también llega en «emitidas», pero ahí el usuario es el
 * receptor: no es suya.
 */
const HERU_SELF_INVOICE_ISSUER_RFC = 'KAP1905278S9';

const CANCELLED_STATUSES = new Set(['CANCELADA', 'CANCELADO']);

const INVOICE_TYPES = new Set<string>(['I', 'E', 'P', 'T', 'N']);

const isInvoiceType = (value: string): value is MyInvoiceType => INVOICE_TYPES.has(value);

const isPaymentMethod = (value: string): value is MyInvoicePaymentMethod =>
  PAYMENT_METHODS.some((method) => method.code === value);

/** `XXX` es «sin moneda» (P y T): el monto se lee en pesos. */
const currencyOf = (raw?: string | null) => {
  const code = raw?.trim().toUpperCase();
  return code && code !== 'XXX' ? code : 'MXN';
};

const SOURCES = new Set<string>(['heru', 'otra_cuenta', 'externa']);

/** Un servidor que todavía no manda `invoice_source` solo distingue lo de esta cuenta. */
const sourceOf = (raw: UnifiedEmittedInvoice): MyInvoiceSource => {
  const source = raw.invoice_source?.trim() ?? '';
  if (SOURCES.has(source)) return source as MyInvoiceSource;
  return raw.IS_HERU_INVOICE === true ? 'heru' : 'externa';
};

const CANCELLATION_STATUSES = new Set<string>(['solicitada', 'confirmada', 'rechazada']);

const cancellationOf = (raw: UnifiedEmittedInvoice): MyInvoice['cancellation'] => {
  const status = raw.cancellation_status?.trim() ?? '';
  return CANCELLATION_STATUSES.has(status) ? (status as MyInvoice['cancellation']) : undefined;
};

/** Una solicitud en curso no la cancela: sigue vigente ante el SAT y se pinta «cancelando». */
const statusOf = (raw: UnifiedEmittedInvoice, cancellation: MyInvoice['cancellation']): MyInvoiceStatus => {
  if (raw.cancelled_at || CANCELLED_STATUSES.has((raw.status ?? '').toUpperCase()) || cancellation === 'confirmada') {
    return 'cancelada';
  }
  return cancellation === 'solicitada' ? 'cancelando' : 'vigente';
};

export const byNewest = (a: MyInvoice, b: MyInvoice) => dayjs(b.issuedAt).valueOf() - dayjs(a.issuedAt).valueOf();

/**
 * Las facturas de un mes de invoices-unified. Las filas de la extracción llegan una por
 * concepto, así que se juntan por folio. Una recibida es de la extracción: nunca lleva
 * sello de heru ni pagos relacionados.
 */
const toListedInvoices = (rows: UnifiedEmittedInvoice[], period: string, kind: 'emitida' | 'recibida'): MyInvoice[] => {
  const byUuid = new Map<string, MyInvoice>();
  for (const raw of rows) {
    if (!raw.uuid || !raw.issued_at) continue;
    const cancellation = cancellationOf(raw);
    const type = raw.invoice_type?.trim().toUpperCase() ?? '';
    const method = raw.payment_method?.trim().toUpperCase() ?? '';
    const source = kind === 'emitida' ? sourceOf(raw) : 'externa';
    const invoice: MyInvoice = {
      kind,
      uuid: raw.uuid,
      period,
      receiverName: raw.receiver_name ?? '',
      receiverRfc: raw.receiver_rfc ?? '',
      issuerName: kind === 'recibida' ? (raw.issuer_name ?? '') : undefined,
      issuerRfc: kind === 'recibida' ? (raw.issuer_rfc ?? '') : undefined,
      description: raw.description?.trim() ?? '',
      total: raw.total ?? 0,
      currency: currencyOf(raw.currency),
      type: isInvoiceType(type) ? type : undefined,
      paymentMethod: isPaymentMethod(method) ? method : undefined,
      stampedByHeru: source !== 'externa',
      source,
      issuedAt: raw.issued_at,
      status: statusOf(raw, cancellation),
      cancelledAt: raw.cancelled_at || undefined,
      cancellation,
      pdfUrl: raw.pdf || undefined,
      xmlUrl: raw.xml || undefined,
      related: (raw.related ?? []).flatMap((relation) => toRelation(relation) ?? []).sort(byPaymentOrder),
    };
    const key = raw.uuid.toLowerCase();
    const seen = byUuid.get(key);
    if (!seen || (invoice.status === 'cancelada' && seen.status !== 'cancelada')) byUuid.set(key, invoice);
  }
  return [...byUuid.values()].sort(byNewest);
};

/** Lo que el usuario emitió en ese mes, con heru o fuera. */
export const toMyInvoices = (emitidas: UnifiedEmittedInvoice[] = [], period: string): MyInvoice[] =>
  toListedInvoices(
    emitidas.filter((raw) => raw.issuer_rfc && raw.issuer_rfc.toUpperCase() !== HERU_SELF_INVOICE_ISSUER_RFC),
    period,
    'emitida',
  );

/** Lo que el usuario recibió en ese mes, según la extracción del SAT. */
export const toReceivedInvoices = (recibidas: UnifiedEmittedInvoice[] = [], period: string): MyInvoice[] =>
  toListedInvoices(recibidas, period, 'recibida');

const RETENTION_SUMMARY = 'Constancia de retenciones';

/**
 * Una constancia de retenciones en la forma del listado. El monto que se pinta es lo que
 * le retuvieron, como en el listado legacy; no tiene tipo de comprobante ni moneda propia.
 */
export const retentionToMyInvoice = (retention: Retention, period: string): MyInvoice => ({
  kind: 'retencion',
  uuid: retention.uuid,
  period,
  receiverName: retention.receiver.name,
  receiverRfc: retention.receiver.rfc,
  issuerName: retention.issuer.name ?? '',
  issuerRfc: retention.issuer.rfc ?? '',
  description: RETENTION_SUMMARY,
  total: retention.totalRetained,
  currency: 'MXN',
  stampedByHeru: false,
  source: 'externa',
  issuedAt: retention.date,
  status: retention.status === 'cancelado' ? 'cancelada' : 'vigente',
  pdfUrl: retention.pdfUrl,
  xmlUrl: retention.xmlUrl,
  related: [],
});

export interface InvoicePeriod {
  year: number;
  month: number;
}

/**
 * El mes en curso con los cortes del servidor. El Go filtra `created_at` por
 * mes en UTC (`periodToDateRange`), así que empezar en el mes de CDMX dejaría
 * fuera lo timbrado en las últimas seis horas de cada mes.
 */
export const currentPeriod = (now = new Date()): InvoicePeriod => ({
  year: now.getUTCFullYear(),
  month: now.getUTCMonth() + 1,
});

export const previousPeriod = ({ year, month }: InvoicePeriod): InvoicePeriod =>
  month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 };

export const periodKeyOf = ({ year, month }: InvoicePeriod) => `${year}-${String(month).padStart(2, '0')}`;

export const periodFromKey = (key: string | undefined): InvoicePeriod | null => {
  const match = /^(\d{4})-(\d{2})$/.exec(key ?? '');
  const month = Number(match?.[2]);
  return match && month >= 1 && month <= 12 ? { year: Number(match[1]), month } : null;
};

/** «agosto 2026». */
export const periodLabel = ({ year, month }: InvoicePeriod) =>
  dayjs(new Date(year, month - 1, 1)).locale('es').format('MMMM YYYY');

export const STATUS_CHIPS: Record<Exclude<MyInvoiceStatus, 'vigente'>, string> = {
  cancelando: 'Cancelación en proceso',
  cancelada: 'Cancelada',
};

export const CANCEL_REJECTED_MESSAGE = 'El SAT no aceptó la cancelación. La factura sigue vigente.';

/** La contraparte: a quién le facturó en una emitida, quién le facturó o le retuvo en las otras. */
const counterpartyRfcOf = (invoice: MyInvoice) =>
  invoice.kind === 'emitida' ? invoice.receiverRfc : (invoice.issuerRfc ?? '');

const counterpartyNameOf = (invoice: MyInvoice) =>
  invoice.kind === 'emitida' ? invoice.receiverName : (invoice.issuerName ?? '');

/**
 * Las facturas en el orden en que se ven con esa búsqueda (por nombre o RFC de la
 * contraparte): también la secuencia del paginador del detalle.
 */
export const visibleSequenceOf = (invoices: MyInvoice[], query: string): MyInvoice[] =>
  invoices.filter((invoice) =>
    matchesAnyField([counterpartyNameOf(invoice), counterpartyRfcOf(invoice), invoice.uuid], query),
  );

/**
 * Lo que cuenta como dinero en un mes: vigente y en pesos; en facturas, solo las de
 * ingreso (un complemento repetiría el monto de su PPD); en retenciones, lo retenido.
 */
const countsAsMonthlyAmount = (invoice: MyInvoice) =>
  invoice.status !== 'cancelada' &&
  invoice.currency === 'MXN' &&
  (invoice.kind === 'retencion' || !invoice.type || invoice.type === 'I');

/** Lo facturado en una lista, con la misma regla que suma cada mes; cuántas cuentan y cuánto suman. */
export const incomeTotalsOf = (invoices: MyInvoice[]) => {
  const counted = invoices.filter(countsAsMonthlyAmount);
  return { count: counted.length, total: counted.reduce((sum, invoice) => sum + invoice.total, 0) };
};

/** Lo que dice un mes cerrado: cuántas trae y cuánto suman las que cuentan como dinero; sin nada que sumar, solo cuántas. */
export const monthSummaryOf = (invoices: MyInvoice[]): string => {
  const kind = invoices[0]?.kind ?? 'emitida';
  const noun = kind === 'retencion' ? ['constancia', 'constancias'] : ['factura', 'facturas'];
  const total = invoices.filter(countsAsMonthlyAmount).reduce((sum, invoice) => sum + invoice.total, 0);
  const count = `${invoices.length} ${invoices.length === 1 ? noun[0] : noun[1]}`;
  return total > 0 ? `${count} · ${formatAmount(total, 'MXN')}` : count;
};

/**
 * Lo facturado en cada mes de un año, con la misma regla que el total de cada mes del
 * listado, de enero al mes de hoy si es el año en curso. `null` si en todo el año no
 * hubo nada: no hay gráfica que enseñar.
 */
export const incomeByMonthOfInvoices = (invoices: MyInvoice[], year: number, now = new Date()): IncomeMonth[] | null => {
  const current = dayjs(now).format('YYYY-MM');
  const byMonth = new Map(
    Array.from({ length: 12 }, (_, index) => `${year}-${String(index + 1).padStart(2, '0')}`)
      .filter((monthKey) => monthKey <= current)
      .map((monthKey) => [monthKey, { monthKey, total: 0, count: 0 }]),
  );
  let hasIncome = false;
  invoices.filter(countsAsMonthlyAmount).forEach((invoice) => {
    const month = byMonth.get(dayjs(invoice.issuedAt).format('YYYY-MM'));
    if (!month) return;
    month.total += invoice.total;
    month.count += 1;
    hasIncome = true;
  });
  return hasIncome ? [...byMonth.values()] : null;
};

export interface MonthGroup {
  key: string;
  /** «septiembre 2026»; la mayúscula inicial la pone el estilo, como en declaraciones. */
  title: string;
  rows: MyInvoice[];
}

/** Por mes de emisión, en el orden en que llegan (ya vienen de la más nueva a la más vieja). */
export const monthGroupsOf = (invoices: MyInvoice[]): MonthGroup[] => {
  const groups = new Map<string, MonthGroup>();
  for (const invoice of invoices) {
    const month = dayjs(invoice.issuedAt);
    const key = month.format('YYYY-MM');
    const group = groups.get(key) ?? { key, title: month.locale('es').format('MMMM YYYY'), rows: [] };
    group.rows.push(invoice);
    groups.set(key, group);
  }
  return [...groups.values()];
};

export const INVOICE_TYPE_LABELS: Record<MyInvoiceType, string> = {
  I: 'Factura',
  E: 'Nota de crédito',
  P: 'Complemento de pago',
  T: 'Traslado',
  N: 'Recibo de nómina',
};

/**
 * Hacia dónde va el dinero, visto desde el usuario: en lo que emitió, una factura y
 * un complemento son cobros (`+`, en azul) y una nota de crédito y un recibo de
 * nómina, dinero que sale (`−`); en lo que recibió es al revés. Un traslado no
 * mueve dinero, sin tipo no se afirma ninguno de los dos, y lo retenido no es un
 * cobro ni una salida.
 */
export type InvoiceFlow = 'cobro' | 'salida' | 'neutro';

const FLOWS: Record<MyInvoiceType, InvoiceFlow> = { I: 'cobro', P: 'cobro', E: 'salida', N: 'salida', T: 'neutro' };

const RECEIVED_FLOWS: Record<InvoiceFlow, InvoiceFlow> = { cobro: 'salida', salida: 'cobro', neutro: 'neutro' };

export const invoiceFlowOf = (invoice: MyInvoice): InvoiceFlow => {
  if (invoice.kind === 'retencion' || !invoice.type) return 'neutro';
  return invoice.kind === 'recibida' ? RECEIVED_FLOWS[FLOWS[invoice.type]] : FLOWS[invoice.type];
};

const SIGNS: Record<InvoiceFlow, string> = { cobro: '+', salida: '−', neutro: '' };

/**
 * «+$103.45», «−$500.00», «+$1,200.00 USD»: el código solo cuando no son
 * pesos, porque el dólar comparte el `$`. `null` en cero: un traslado no mueve
 * dinero, y un complemento cuyo pago no se pudo leer no pagó $0.00.
 */
export const formatInvoiceAmount = (invoice: MyInvoice): string | null => {
  if (invoice.total === 0) return null;
  const code = invoice.currency === 'MXN' ? '' : ` ${invoice.currency}`;
  return `${SIGNS[invoiceFlowOf(invoice)]}${formatAmount(Math.abs(invoice.total), invoice.currency)}${code}`;
};

const AMOUNT_TONES: Record<InvoiceFlow, string> = {
  cobro: 'text-h-primary-50',
  salida: 'text-h-text-heading',
  neutro: 'text-h-text-heading',
};

/** El tono del monto, como en declaraciones; cancelada, tachada y en gris, porque ya no cuenta. */
export const amountToneOf = (invoice: MyInvoice) =>
  invoice.status === 'cancelada'
    ? 'text-h-text-light font-medium line-through decoration-1'
    : AMOUNT_TONES[invoiceFlowOf(invoice)];

/**
 * Sin PDF guardado (lo timbrado fuera de heru), el fiscal-engine lo pinta desde el XML.
 * No sabe pintar una constancia de retenciones ni una nómina.
 */
export const isPdfFromXml = (invoice: MyInvoice) =>
  !invoice.pdfUrl && invoice.kind !== 'retencion' && invoice.type !== 'N';

/** El nombre de la contraparte; sin nombre en el comprobante, su RFC. */
export const counterpartyOf = (invoice: MyInvoice) =>
  displayFiscalName(counterpartyNameOf(invoice)) || counterpartyRfcOf(invoice);

const PDF_TITLES: Record<MyInvoice['kind'], string> = {
  emitida: 'Tu factura',
  recibida: 'Factura recibida',
  retencion: 'Constancia de retenciones',
};

export type PdfFromXmlDocument = { uuid: string; title: string; subtitle: string; fileName: string };

export const invoicePdfDocument = (invoice: MyInvoice): PdfFromXmlDocument => ({
  uuid: invoice.uuid,
  title: PDF_TITLES[invoice.kind],
  subtitle: counterpartyOf(invoice),
  fileName: `factura-${invoice.uuid}.pdf`,
});

/**
 * La segunda línea de la fila: el concepto. Lo que no es una factura de ingreso
 * se anuncia primero; el concepto de un complemento no se repite porque el SAT
 * lo obliga a decir «Pago».
 */
export const invoiceSummaryOf = (invoice: MyInvoice): string => {
  if (invoice.kind === 'retencion' || !invoice.type || invoice.type === 'I') return invoice.description;
  if (invoice.type === 'P') return INVOICE_TYPE_LABELS.P;
  return [INVOICE_TYPE_LABELS[invoice.type], invoice.description].filter(Boolean).join(' · ');
};

export const isInstallmentInvoice = (invoice: MyInvoice) => invoice.type === 'I' && invoice.paymentMethod === 'PPD';

/**
 * Cómo va el cobro de una PPD, leído del último complemento que se conoce: sin
 * pagos, «En parcialidades»; con saldo, cuánto falta; en cero, liquidada. El
 * saldo es el que dice ese complemento, no un cálculo nuestro.
 */
export const installmentStatusOf = (invoice: MyInvoice): { label: string; settled: boolean } | null => {
  if (invoice.kind !== 'emitida' || !isInstallmentInvoice(invoice) || invoice.status !== 'vigente') return null;
  const last = invoice.related.filter((relation) => relation.type === 'P').at(-1);
  if (last?.remainingBalance === undefined) return { label: 'En parcialidades', settled: false };
  if (last.remainingBalance <= 0) return { label: 'Liquidada', settled: true };
  const code = invoice.currency === 'MXN' ? '' : ` ${invoice.currency}`;
  return { label: `Por cobrar ${formatAmount(last.remainingBalance, invoice.currency)}${code}`, settled: false };
};

/** Un monto de la relación en la moneda de la factura que se está viendo. */
export const formatRelatedAmount = (amount: number, currency: string) =>
  `${formatAmount(amount, currency)}${currency === 'MXN' ? '' : ` ${currency}`}`;

export const paymentMethodLabel = (method: MyInvoicePaymentMethod) =>
  `${PAYMENT_METHODS.find((candidate) => candidate.code === method)?.name} (${method})`;

/** Solo cuando no son pesos: «Dólares (USD)». */
export const foreignCurrencyLabel = (currency: string): string | undefined => {
  if (currency === 'MXN') return undefined;
  const name = CURRENCIES.find((candidate) => candidate.code === currency)?.name;
  return name ? `${name} (${currency})` : currency;
};

export const formatInvoiceDay = (iso: string) => dayjs(iso).locale('es').format('D MMM').replace('.', '');

/** «29 de septiembre»; con año solo si no es el de hoy. */
export const formatInvoiceDayLong = (iso: string) => {
  const date = dayjs(iso).locale('es');
  return date.format(date.year() === dayjs().year() ? 'D [de] MMMM' : 'D [de] MMMM [de] YYYY');
};

export const formatInvoiceDate = (iso: string) => dayjs(iso).locale('es').format('D [de] MMMM [de] YYYY');

/** «septiembre», para decir en qué declaración cuenta. */
export const invoiceMonthName = (iso: string) => dayjs(iso).locale('es').format('MMMM');
